import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Cookie, Settings, Clock, AlertCircle, RefreshCw } from 'lucide-react';

export default function PolitiqueCookies() {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollIntoView({ block: 'start', behavior: 'instant' });
    }
  }, []);

  return (
    <>
      <Helmet>
        <title>Politique des Cookies | Elite Transfert</title>
        <meta name="description" content="Politique des cookies d'Elite Transfert - Service VTC de luxe à Paris et en Île-de-France" />
        <link rel="canonical" href="https://elite-transfer.fr/politique-cookies" />
      </Helmet>
      
      <div ref={contentRef} className="min-h-screen bg-[#121212] text-white py-16 px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-4xl mx-auto"
        >
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-6 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              Politique des Cookies
              <span className="block text-xl mt-2 bg-gradient-to-r from-yellow-500 to-yellow-600 bg-clip-text">
                Gestion et utilisation des cookies
              </span>
            </h1>
          </div>
          
          <div className="bg-gray-900/60 backdrop-blur-sm rounded-xl p-8 shadow-xl border border-gray-800">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="space-y-8"
            >
              <div className="flex items-start gap-4">
                <Cookie className="w-8 h-8 text-yellow-500 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-2xl font-semibold text-yellow-500 mb-4">1. Qu'est-ce qu'un cookie ?</h2>
                  <p className="text-gray-300 mb-4">
                    Un cookie est un petit fichier texte déposé sur votre terminal (ordinateur, tablette ou mobile) lors de la visite d'un site web. Il permet au site de mémoriser des informations sur votre visite, comme votre langue préférée et d'autres paramètres.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Settings className="w-8 h-8 text-yellow-500 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-2xl font-semibold text-yellow-500 mb-4">2. Les cookies que nous utilisons</h2>
                  <p className="text-gray-300 mb-4">
                    Nous utilisons différents types de cookies :
                  </p>
                  <ul className="list-disc pl-6 mb-6 text-gray-300 space-y-2">
                    <li>Cookies essentiels : nécessaires au fonctionnement du site</li>
                    <li>Cookies de performance : pour analyser la façon dont les visiteurs utilisent notre site</li>
                    <li>Cookies de fonctionnalité : pour mémoriser vos préférences</li>
                    <li>Cookies de ciblage : pour personnaliser votre expérience</li>
                  </ul>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <AlertCircle className="w-8 h-8 text-yellow-500 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-2xl font-semibold text-yellow-500 mb-4">3. Gestion des cookies</h2>
                  <p className="text-gray-300 mb-4">
                    Vous pouvez à tout moment choisir de désactiver ces cookies. Votre navigateur peut également être paramétré pour vous signaler les cookies qui sont déposés dans votre terminal et vous demander de les accepter ou non.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Clock className="w-8 h-8 text-yellow-500 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-2xl font-semibold text-yellow-500 mb-4">4. Durée de conservation</h2>
                  <p className="text-gray-300 mb-4">
                    Les cookies sont conservés pour une durée maximale de 13 mois. À l'expiration de ce délai, votre consentement sera à nouveau nécessaire.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <RefreshCw className="w-8 h-8 text-yellow-500 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-2xl font-semibold text-yellow-500 mb-4">5. Modification de la politique</h2>
                  <p className="text-gray-300 mb-4">
                    Nous nous réservons le droit de modifier cette politique de cookies à tout moment. Toute modification prendra effet immédiatement.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </>
  );
}
